import { retrieveImageSearchList } from '@/api/product'
import { useLoginModal } from '@/components/Login'
import { useImageCropper } from '@/hooks/useImageCropper'
import { isMobile } from '@/utils/constants'
import { buriedPointUtil, gaAnalytics } from '@/utils/utils'
import { createSharedComposable } from '@vueuse/core'

/**
 * @returns {object}
 * filter - 返回过滤项配置
 * loading - 当前是否处于加载中
 * finished - 是否已加载完成，加载完成后不再触发 onLoad 事件
 * list - 返回列表数据
 * onLoad - 加载更多
 * onReset - 重置 page / loading / finished 状态并触发 onLoad 事件
 */
export const useProductList = createSharedComposable(() => {
  const route = useRoute()
  const { show: showLoginModal, open: openLoginModal } = useLoginModal()

  const loading = ref(false)
  const finished = ref(false)

  const filter = reactive({
    imageAddress: sessionStorage.getItem(`currentMainImage_${route.query.imageId}`) ||
    sessionStorage.getItem(`currentMainImage_${route.query.offerId}`) ||
    route.query.imageUrl ||
    '',
    imageId: route.query.imageId ?? 0,
    region: null
  })
  const page = reactive({
    pageNow: 1,
    pageSize: 50
  })

  const list = shallowRef([])

  const croppers = shallowRef([])

  const { cropper } = useImageCropper()

  /**
   * 防止请求竞态导致列表数据展示不正确问题
   */
  let latestRequestId = 0

  const onLoad = async () => {
    /**
     * 当出现登录弹窗时，不再触发 onLoad
     */
    if (showLoginModal.value) return
    if (loading.value || finished.value) return
    try {
      const currentRequestId = ++latestRequestId
      const { keyword, ...rest } = filter
      const data = { searchKeyWord: keyword, ...rest, ...page }
      loading.value = true
      const rows = await getList(data)

      if (currentRequestId === latestRequestId) {
        if (rows.length > 0) {
          list.value = [...list.value, ...rows]
          page.pageNow++
        } else {
          finished.value = true
        }
        /**
         * 不用 finally 修改 loading 状态是因为 当 请求竞态 出现时，finally 会导致 loading 状态不正确
         */
        loading.value = false
      }
    } catch (e) {
      console.error(e)
      loading.value = false
    }
  }

  const getList = async (filter) => {
    const { code, data, msg } = await retrieveImageSearchList({
      ...filter
    }, {
      manualHandle401Error: filter.pageNow > 1
    })
    if (code === 401 && filter.pageNow > 1) {
      openLoginModal()
    }
    if (code !== 200) {
      throw new Error(msg)
    }
    if (croppers.value.length === 0) {
      croppers.value = await cropper(filter.imageAddress, formatCropDimensions(data.picRegionInfo.yoloCropRegion))
    }
    if (filter.pageNow > 1) {
      if (isMobile) {
        buriedPointUtil({ eventId: 'MPSC401', others: { ...filter } })
        gaAnalytics('mp_search_page_load_more', 'mp_search_page_load_more')
      } else {
        buriedPointUtil({ eventId: 'PSC401', others: { ...filter } })
      }
    }
    return data.rows
  }

  const formatCropDimensions = (str) => {
    return str.split(';').map(region => region.split(','))
  }

  const onReset = () => {
    page.pageNow = 1
    list.value = []
    loading.value = false
    finished.value = false
    onLoad()
  }

  onBeforeMount(() => {
    onLoad()
  })

  return { filter, loading, finished, list, croppers, onLoad, onReset }
})
